import React, { useEffect, useState } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient as defaultClient } from './queryClient';
import { defaultPersistOptions } from './persister';
import { defaultBroadcastOptions, startQueryBroadcast } from './queryBroadcast';

/**
 * Application Query Provider with Persistent Storage Cache
 * 
 * Uses TanStack Query v5 PersistQueryClientProvider to hydrate and persist
 * server state to storage across browser reloads, network switches, and tab restarts.
 */
export const QueryProvider = ({
  client = defaultClient,
  children,
  persist = true,
  persistOptions = defaultPersistOptions,
  broadcast = true,
  broadcastOptions = defaultBroadcastOptions,
  showDevtools = true,
  onHydrated,
}) => {
  const [isHydrated, setIsHydrated] = useState(!persist);

  useEffect(() => {
    if (!broadcast) return undefined;
    return startQueryBroadcast(client, broadcastOptions);
  }, [broadcast, broadcastOptions, client]);

  const handleSuccess = () => {
    setIsHydrated(true);
    if (onHydrated) {
      onHydrated(client);
    }
  };

  if (!persist) {
    return (
      <QueryClientProvider client={client}>
        {children}
        {showDevtools && import.meta.env.DEV && (
          <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
        )}
      </QueryClientProvider>
    );
  }

  return (
    <PersistQueryClientProvider
      client={client}
      persistOptions={persistOptions}
      onSuccess={handleSuccess}
    >
      {children}
      {showDevtools && import.meta.env.DEV && (
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
      )}
    </PersistQueryClientProvider>
  );
};

export default QueryProvider;
